import React, { useEffect, useState } from 'react';
import SearchBox from './SearchBox';
import { matchesQuery } from '../utils/search';
import toast from 'react-hot-toast';

import CatalogService from '../services/catalog.service';
import SubCatalogService from '../services/subcatalog.service';
import { useTranslation } from 'react-i18next';
import Spinner from './Spinner';

const emptyForm = { name: '', description: '', catalog_id: '' };

const CreateSubCatalog = () => {
  const { t } = useTranslation();
  const [subCatalogs, setSubCatalogs] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [catalogFilter, setCatalogFilter] = useState('');

  const resolveMsg = (err) => (err.response && err.response.data && err.response.data.message) || err.message || t('product.errors.generic');

  const load = () => {
    setLoading(true);
    SubCatalogService.getAll()
      .then((res) => setSubCatalogs(res.data))
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setLoading(false));
  };

  const loadCatalogs = () => {
    CatalogService.getAll()
      .then((res) => setCatalogs(res.data))
      .catch((err) => toast.error(resolveMsg(err)));
  };

  useEffect(() => {
    loadCatalogs();
    load();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage('');

    if (!form.name.trim()) {
      toast.error(t('createSubCatalog.nameRequired'));
      return;
    }
    if (!form.catalog_id) {
      toast.error(t('createSubCatalog.catalogRequired'));
      return;
    }

    const action = editingId ? SubCatalogService.update(editingId, form) : SubCatalogService.create(form);

    action
      .then(() => {
        setMessage(editingId ? t('createSubCatalog.updated') : t('createSubCatalog.created'));
        resetForm();
        load();
      })
      .catch((err) => toast.error(resolveMsg(err)));
  };

  const handleEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name || '',
      description: s.description || '',
      catalog_id: s.catalog_id || '',
    });
    setMessage('');
  };

  const handleDelete = (id) => {
    if (!window.confirm(t('createSubCatalog.confirmDelete'))) return;
    setMessage('');
    SubCatalogService.remove(id)
      .then(() => {
        setMessage(t('createSubCatalog.deleted'));
        if (editingId === id) resetForm();
        load();
      })
      .catch((err) => toast.error(resolveMsg(err)));
  };

  const catalogName = (s) => (s.catalog && s.catalog.name) || (catalogs.find((c) => c.id === s.catalog_id) || {}).name || s.catalog_id;

  const filteredSubs = subCatalogs.filter((s) => {
    const parentId = s.catalog_id ?? (s.catalog && s.catalog.id);
    return (
      (!catalogFilter || String(parentId) === catalogFilter) &&
      matchesQuery(query, s.name, s.description, catalogName(s))
    );
  });

  return (
    <div className='container mt-4'>
      <h3>{editingId ? t('createSubCatalog.editTitle') : t('createSubCatalog.createTitle')}</h3>

      {message && <div className='alert alert-success'>{message}</div>}

      <form onSubmit={handleSubmit} className='card card-body mb-4'>
        <div className='mb-3'>
          <label className='form-label'>{t('createSubCatalog.catalog')} *</label>
          <select className='form-select' name='catalog_id' value={form.catalog_id} onChange={handleChange}>
            <option value=''>{t('createSubCatalog.selectCatalog')}</option>
            {catalogs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className='mb-3'>
          <label className='form-label'>{t('product.nameRequired')}</label>
          <input type='text' className='form-control' name='name' value={form.name} onChange={handleChange} placeholder={t('createSubCatalog.namePlaceholder')} />
        </div>
        <div className='mb-3'>
          <label className='form-label'>{t('common.description')}</label>
          <textarea className='form-control' name='description' value={form.description} onChange={handleChange} rows={2} placeholder={t('product.descriptionPlaceholder')} />
        </div>
        <div className='d-flex gap-2'>
          <button type='submit' className='btn btn-primary'>
            {editingId ? t('common.update') : t('common.create')}
          </button>
          {editingId && (
            <button type='button' className='btn btn-secondary' onClick={resetForm}>
              {t('common.cancel')}
            </button>
          )}
        </div>
      </form>

      <h4>{t('createSubCatalog.listTitle')}</h4>
      {loading ? (
        <Spinner />
      ) : subCatalogs.length === 0 ? (
        <p className='text-muted'>{t('createSubCatalog.empty')}</p>
      ) : (
        <>
        <div className='row g-2 mb-3'>
          <div className='col-md-8'>
            <SearchBox
              value={query}
              onChange={setQuery}
              placeholder={t('createSubCatalog.searchPlaceholder', 'Search sub-catalogs by name or description...')}
            />
          </div>
          <div className='col-md-4'>
            <select
              className='form-select'
              value={catalogFilter}
              onChange={(e) => setCatalogFilter(e.target.value)}
              aria-label={t('createSubCatalog.catalog')}
            >
              <option value=''>{t('createSubCatalog.allCatalogs', 'All catalogs')}</option>
              {catalogs.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        {filteredSubs.length === 0 ? (
          <p className='text-muted'>{t('common.noResults', 'No results match your search.')}</p>
        ) : (
        <div className='table-responsive rounded'>
          <table className='table table-hover'>
            <thead>
              <tr>
                <th style={{ width: 60 }}>{t('common.id')}</th>
                <th>{t('createSubCatalog.catalog')}</th>
                <th>{t('common.name')}</th>
                <th>{t('common.description')}</th>
                <th style={{ width: 160 }}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubs.map((s) => (
                <tr key={s.id}>
                  <td>{s.id}</td>
                  <td>{catalogName(s)}</td>
                  <td>{s.name}</td>
                  <td>{s.description}</td>
                  <td>
                    <button className='btn btn-sm btn-outline-primary me-2' onClick={() => handleEdit(s)}>
                      {t('common.edit')}
                    </button>
                    <button className='btn btn-sm btn-outline-danger' onClick={() => handleDelete(s.id)}>
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}
    </div>
  );
};

export default CreateSubCatalog;
